/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { connect } from 'react-redux'

import Feature from '@modules/utils/feature'
import withRouter from '@modules/routing/withRouter'

import { fetchVersions, deleteVersion, fetchWhitelistedVersions } from '@/actions/elasticStack'
import {
  removeWhitelistedVersionRequest,
  fetchWhitelistedVersionRequest,
  fetchVersionsRequest,
  deleteStackVersionRequest,
  addWhitelistedVersionRequest,
} from '@/reducers/asyncRequests/registry'
import { getActiveVersionStacks } from '@/reducers/elasticStack'
import { getRegion } from '@/reducers/regions'
import { isFeatureActivated } from '@/selectors'

import withPolling from '../../../lib/withPolling'

import ElasticStackVersions from './ElasticStackVersions'

import type { ConsumerProps, DispatchProps, StateProps } from './types'
import type { ReduxState, ThunkDispatch } from '@/types/redux'

const mapStateToProps = (state: ReduxState, { router: { params } }: ConsumerProps): StateProps => ({
  addWhitelistedVersionRequest: addWhitelistedVersionRequest(state, params.regionId),
  currentVersion: params.versionId,
  fetchWhitelistedVersionRequest: fetchWhitelistedVersionRequest(state, params.regionId),
  getDeleteVersionRequest: (regionId, versionId) =>
    deleteStackVersionRequest(state, regionId, versionId),
  isWhitelistingEnabled: isFeatureActivated(state, Feature.whitelistingStack),
  region: getRegion(state, params.regionId),
  regionId: params.regionId,
  removeWhitelistedVersionRequest: removeWhitelistedVersionRequest(state, params.regionId),
  versions: getActiveVersionStacks(state, params.regionId),
  versionsRequest: fetchVersionsRequest(state, params.regionId),
})

const pollingComponent = withPolling<ConsumerProps>(
  ElasticStackVersions,
  // eslint-disable-next-line no-shadow
  ({ fetchVersions, fetchWhitelistedVersions, isWhitelistingEnabled, router: { params } }) => ({
    onPoll: [
      () => fetchVersions(params.regionId),
      () =>
        isWhitelistingEnabled
          ? fetchWhitelistedVersions({ regionId: params.regionId })
          : Promise.resolve(),
    ],
    pollImmediately: [[`router`, `params`, `regionId`]],
  }),
)

const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => ({
  deleteVersion: (version, region) => dispatch(deleteVersion(version, region)),
  fetchVersions: (regionId) => dispatch(fetchVersions(regionId)),
  fetchWhitelistedVersions: (args) => dispatch(fetchWhitelistedVersions(args)),
})

export default withRouter(
  connect<StateProps, DispatchProps, ConsumerProps, ReduxState>(
    mapStateToProps,
    mapDispatchToProps,
  )(pollingComponent),
)
